/**
 * Table view of the products list — the alternative to the card grid,
 * enabled via the ViewToggleAndCart toggle. Shows one row per product
 * (not grouped) with columns for code / brand / product / thickness /
 * grade / rate / Add button.
 *
 * When the current search returns zero products, an inline "Quick Add"
 * strip lets staff turn the search string into a new catalog entry via
 * the Laminate / Highlighter / Hardware / Adhesive Quick Add dialogs,
 * pre-populating the `product_code` field with whatever they typed.
 *
 * Extracted from ProductsPage.js. Pure JSX with the
 * parent's state + setters passed as props — 9 total. Chunky prop
 * signature is a deliberate trade-off vs. bringing context/reducer
 * plumbing into what is otherwise a leaf presentational component.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus, Palette, Sparkles, Wrench, Pipette } from 'lucide-react';
import { formatINR } from '../../../lib/store';


function EmptyStateQuickAddRow({
  search,
  onQuickAddLaminate,
  onQuickAddHighlighter,
  onQuickAddHardware,
  onQuickAddAdhesive,
}) {
  return (
    <tr>
      <td colSpan={7} className="text-center py-8">
        <div className="text-heda-mute mb-4">No products found for &quot;{search}&quot;</div>
        <div className="flex flex-wrap justify-center gap-2">
          <span className="text-xs text-heda-sub">Quick Add:</span>
          <Button size="sm" variant="outline"
            className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            onClick={onQuickAddLaminate}
            data-testid="table-empty-quick-add-laminate">
            <Palette className="w-3 h-3 mr-1" /> Laminate
          </Button>
          <Button size="sm" variant="outline"
            className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            onClick={onQuickAddHighlighter}
            data-testid="table-empty-quick-add-highlighter">
            <Sparkles className="w-3 h-3 mr-1" /> Highlighter
          </Button>
          <Button size="sm" variant="outline"
            className="rounded-xl h-7 text-xs border-heda-sub text-heda-sub"
            onClick={onQuickAddHardware}
            data-testid="table-empty-quick-add-hardware">
            <Wrench className="w-3 h-3 mr-1" /> Hardware
          </Button>
          <Button size="sm" variant="outline"
            className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            onClick={onQuickAddAdhesive}
            data-testid="table-empty-quick-add-adhesive">
            <Pipette className="w-3 h-3 mr-1" /> Adhesive
          </Button>
        </div>
      </td>
    </tr>
  );
}


export function ProductsTableView({
  products,
  search,
  onAddProduct,
  onQuickAddLaminate,
  onQuickAddHighlighter,
  onQuickAddHardware,
  onQuickAddAdhesive,
}) {
  return (
    <div className="bg-white border border-heda-line rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead>
            <tr className="heda-thead border-b border-heda-line">
              <th className="text-left py-2 px-3 font-semibold text-heda-sub text-xs uppercase tracking-wide">Code</th>
              <th className="text-left py-2 px-3 font-semibold text-heda-sub text-xs uppercase tracking-wide">Brand</th>
              <th className="text-left py-2 px-3 font-semibold text-heda-sub text-xs uppercase tracking-wide">Product</th>
              <th className="text-left py-2 px-3 font-semibold text-heda-sub text-xs uppercase tracking-wide">Thickness</th>
              <th className="text-left py-2 px-3 font-semibold text-heda-sub text-xs uppercase tracking-wide">Grade</th>
              <th className="text-right py-2 px-3 font-semibold text-heda-sub text-xs uppercase tracking-wide">Rate</th>
              <th className="text-center py-2 px-3 font-semibold text-heda-sub text-xs uppercase tracking-wide">Add</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <EmptyStateQuickAddRow
                search={search}
                onQuickAddLaminate={onQuickAddLaminate}
                onQuickAddHighlighter={onQuickAddHighlighter}
                onQuickAddHardware={onQuickAddHardware}
                onQuickAddAdhesive={onQuickAddAdhesive}
              />
            ) : (
              products.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-heda-line hover:bg-heda-cream transition-colors"
                  data-testid={`product-row-${p.id}`}
                >
                  <td className="py-2 px-3 font-mono text-xs text-heda-olive-dark font-medium">
                    {p.product_code || '-'}
                  </td>
                  <td className="py-2 px-3 font-semibold text-heda-ink">{p.brand_name}</td>
                  <td className="py-2 px-3 text-heda-ink">{p.subcategory || p.product_name}</td>
                  <td className="py-2 px-3 font-mono-data text-xs">{p.thickness}</td>
                  <td className="py-2 px-3 text-xs text-heda-sub">{p.grade}</td>
                  <td className="py-2 px-3 text-right font-mono-data font-semibold text-heda-olive-dark">
                    {formatINR(p.base_rate)}
                    <span className="text-xs text-heda-mute ml-1">
                      /{p.rate_type?.replace('per ', '')}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <Button
                      size="sm"
                      className="rounded-xl h-7 bg-heda-ink hover:bg-black text-white"
                      onClick={() => onAddProduct(p)}
                      data-testid={`add-product-btn-${p.id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
